/* eslint-disable no-case-declarations */
import nestedProperty from 'nested-property';
import { CommentType } from 'types';
import { v4 as uuidv4 } from 'uuid';

import { dateTimeFormat } from './dateTimeFormat';

export function setKey(obj: { [x: string]: any }, path: string, value: any) {
    if (!obj) {
        obj = {};
    }
    nestedProperty.set(obj, path, value);
    return obj; // Return the updated object for chaining
}

export function getKey(obj: { [x: string]: any }, path: string): any {
    if (!obj || !path) return undefined;

    const keys = path.split('.');
    let key = obj;

    for (let index = 0; index < keys.length; index++) {
        if (Array.isArray(key)) {
            const arrayIndex = keys[index] === '-1' ? key.length - 1 : parseInt(keys[index]);
            if (isNaN(arrayIndex) || arrayIndex < 0 || arrayIndex >= key.length) {
                return undefined; // Invalid array index
            }
            key = key[arrayIndex];
        } else {
            if (!key || typeof key !== 'object' || !(keys[index] in key)) {
                return undefined; // Key does not exist
            }
            key = key[keys[index]];
        }
    }

    return key;
}

export function removeKey(obj: { [x: string]: any }, path: string): { [x: string]: any } {
    if (!obj || !path) return obj; // If obj or path is invalid, return the original object

    const keys = path.split('.');
    let key = obj;

    for (let index = 0; index < keys.length - 1; index++) {
        if (Array.isArray(key)) {
            const arrayIndex = keys[index] === '-1' ? key.length - 1 : parseInt(keys[index]);
            if (isNaN(arrayIndex) || arrayIndex < 0 || arrayIndex >= key.length) {
                return obj; // Invalid array index, return the original object
            }
            key = key[arrayIndex];
        } else {
            if (!key || typeof key !== 'object' || !(keys[index] in key)) {
                return obj; // Key does not exist, return the original object
            }
            key = key[keys[index]];
        }
    }

    const finalKey = keys[keys.length - 1];

    if (Array.isArray(key)) {
        const arrayIndex = finalKey === '-1' ? key.length - 1 : parseInt(finalKey);
        if (!isNaN(arrayIndex) && arrayIndex >= 0 && arrayIndex < key.length) {
            key.splice(arrayIndex, 1); // Remove the element from the array
        }
    } else if (key && typeof key === 'object' && finalKey in key) {
        delete key[finalKey]; // Remove the key from the object
    }

    return obj; // Return the updated object for chaining
}

export function getId() {
    return uuidv4();
}

export function parseHtmlEntities(text) {
    if (!text) {
        return text;
    }
    const entities = [
        ['amp', '&'],
        ['apos', "'"],
        ['#x27', "'"],
        ['#x2F', '/'],
        ['#39', "'"],
        ['#47', '/'],
        ['lt', '<'],
        ['gt', '>'],
        ['nbsp', ' '],
        ['quot', '"'],
    ];

    for (let i = 0, max = entities.length; i < max; ++i)
        text = text.replace(new RegExp('&' + entities[i][0] + ';', 'g'), entities[i][1]);

    return text;
}

export function findChanged(obj1: object, obj2: object) {
    const changes: { key: string; value1: any; value2: any }[] = [];

    // Iterate over the keys of the first object
    for (const key in obj1) {
        if (Object.prototype.hasOwnProperty.call(obj1, key) && Object.prototype.hasOwnProperty.call(obj2, key)) {
            // If the values are different
            if (JSON.stringify(obj1[key]) !== JSON.stringify(obj2[key])) {
                changes.push({ key, value1: JSON.stringify(obj1[key]), value2: JSON.stringify(obj2[key]) });
            }
        } else if (
            Object.prototype.hasOwnProperty.call(obj1, key) &&
            !Object.prototype.hasOwnProperty.call(obj2, key)
        ) {
            // Key exists in obj1 but not in obj2
            changes.push({ key, value1: JSON.stringify(obj1[key]), value2: undefined });
        } else if (
            !Object.prototype.hasOwnProperty.call(obj1, key) &&
            Object.prototype.hasOwnProperty.call(obj2, key)
        ) {
            // Key exists in obj2 but not in obj1
            changes.push({ key, value1: undefined, value2: JSON.stringify(obj2[key]) });
        }
    }

    // Iterate over the keys of the second object to check for extra keys
    for (const key in obj2) {
        if (!Object.prototype.hasOwnProperty.call(obj1, key) && Object.prototype.hasOwnProperty.call(obj2, key)) {
            changes.push({ key, value1: undefined, value2: obj2[key] });
        }
    }

    return changes.length > 0 ? changes : [];
}


export function checkDifferent(props: any, state: any, ignoreLoading = false): boolean {
    const normalize = (obj: any): any => {
        if (Array.isArray(obj)) {
            return obj.map(normalize).sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b)));
        }

        if (typeof obj === 'object' && obj !== null) {
            return Object.fromEntries(
                Object.entries(obj)
                    .filter(([key]) => !(ignoreLoading && key === 'loading') && !['isoDate', 'source'].includes(key))
                    .map(([key, value]) => [key, normalize(value)])
            );
        }

        if (typeof obj === 'string') {
            return obj.replace(/\s+/g, '');
        }

        return obj;
    };

    return JSON.stringify(normalize(props)) !== JSON.stringify(normalize(state));
}

export function getMode(mode) {
    switch (mode) {
        case 'search':
            return 'chart';

        case 'playback':
            return 'playback';
        case 'live':
        case 'time':
            return 'time';

        default:
            return 'chart';
    }
}

export function checkLayoutDifferent(props: any, state: any, paths: string[] = []): boolean {
    const getValues = (obj: any, paths: string[]) => paths.map((path) => getKey(obj, path)); // Extract values for each path

    const filteredProps = paths.length > 0 ? getValues(props, paths) : props;
    const filteredState = paths.length > 0 ? getValues(state, paths) : state;
    return JSON.stringify(filteredProps) !== JSON.stringify(filteredState);
}

export function arrayToIndexedObject(arrayData) {
    return arrayData.reduce((acc, obj) => {
        const { id, ...rest } = obj; // Destructure to get id and the rest of the object
        acc[id] = rest; // Add the rest of the object to the accumulator with the id as the key
        return acc;
    }, {});
}

export function cloneArray(arr) {
    return [...arr];
}

export function renderError(error: string | undefined | null): CommentType[] {
    if (!error) {
        return [];
    }
    return [{ uuid: getId(), type: 'error', text: error }];
}

export function cloneObject(obj) {
    const clone = {};
    for (const i in obj) {
        if (obj[i] != null && i === 'icon') {
            clone[i] = obj[i];
        } else if (obj[i] != null && Array.isArray(obj[i])) {
            clone[i] = cloneArray(obj[i]);
        } else if (obj[i] != null && typeof obj[i] == 'object') {
            clone[i] = cloneObject(obj[i]);
        } else {
            clone[i] = obj[i];
        }
    }
    return clone;
}

export function clone(obj: any, stringDate = false): any {
    let cloneObject;

    if (Array.isArray(obj)) {
        // If obj is an array, clone its elements
        cloneObject = [];
        for (let i = 0; i < obj.length; i++) {
            if (obj[i] != null && typeof obj[i] === 'object') {
                if (obj[i] instanceof dateTimeFormat) {
                    cloneObject.push(
                        stringDate ? obj[i].toString() : obj[i].clone() // Return string format or clone the instance
                    );
                } else {
                    cloneObject.push(clone(obj[i], stringDate)); // Recursively clone objects inside the array
                }
            } else {
                cloneObject.push(obj[i]); // Copy primitive values
            }
        }
    } else if (typeof obj === 'object' && obj !== null) {
        // If obj is an object, clone its properties
        if (obj instanceof dateTimeFormat) {
            return stringDate ? obj.toString() : obj.clone(); // Return string format or clone the instance
        } else {
            cloneObject = {};
            for (const key in obj) {
                if (Object.prototype.hasOwnProperty.call(obj, key)) {
                    if (obj[key] != null && typeof obj[key] === 'object') {
                        if (obj[key] instanceof dateTimeFormat) {
                            cloneObject[key] = stringDate ? obj[key].toString() : obj[key].clone(); // Return string format or clone the instance
                        } else {
                            cloneObject[key] = clone(obj[key], stringDate); // Recursively clone nested objects
                        }
                    } else {
                        cloneObject[key] = obj[key]; // Copy primitive values
                    }
                }
            }
        }
    } else {
        // If obj is neither an array nor an object, return it as is
        cloneObject = obj;
    }

    return cloneObject;
}
